/**
 * Which brand the portal renders as. Decided once per page load from the hostname.
 *
 * - `hs`: HealthSource (red theme, HealthSource images). Only on *.healthsourcechiro.com.
 * - `default`: plain blue theme with no vendor branding (patients don't know us), e.g. patientadmin.com.
 *
 * Testing override on any host: open `/hs` (or `/hs/<route>`) or add `?hs` to a URL. The override
 * lasts for the browser tab (sessionStorage). `?hs=0` clears it.
 */
export type Brand = "hs" | "default";

export const HS_DOMAIN = "healthsourcechiro.com";
const OVERRIDE_KEY = "portal.brand";

type BrandLocation = Pick<Location, "hostname" | "pathname" | "search" | "hash">;

export type BrandResolution = {
  brand: Brand;
  /** Path+search+hash with the `/hs` prefix and `hs` query param removed, or null if unchanged. */
  cleanedUrl: string | null;
};

function isHsHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === HS_DOMAIN || host.endsWith(`.${HS_DOMAIN}`);
}

function readOverride(storage: Storage | null): Brand | null {
  try {
    return storage?.getItem(OVERRIDE_KEY) === "hs" ? "hs" : null;
  } catch {
    return null;
  }
}

function writeOverride(storage: Storage | null, brand: Brand | null) {
  try {
    if (brand) storage?.setItem(OVERRIDE_KEY, brand);
    else storage?.removeItem(OVERRIDE_KEY);
  } catch {
    /* storage blocked (private mode); the override just won't persist */
  }
}

/** Pure resolution, testable without a window. Writes the testing override to `storage`. */
export function resolveBrand(loc: BrandLocation, storage: Storage | null): BrandResolution {
  let pathname = loc.pathname;
  const params = new URLSearchParams(loc.search);
  let changed = false;
  let override: Brand | null | undefined; // undefined = no instruction in this URL

  if (/^\/hs(\/|$)/i.test(pathname)) {
    pathname = pathname.slice(3) || "/";
    override = "hs";
    changed = true;
  }
  if (params.has("hs")) {
    const value = (params.get("hs") ?? "").toLowerCase();
    override = value === "0" || value === "false" ? null : "hs";
    params.delete("hs");
    changed = true;
  }
  if (override !== undefined) writeOverride(storage, override);

  const brand: Brand =
    isHsHost(loc.hostname) || (override === undefined ? readOverride(storage) : override) === "hs"
      ? "hs"
      : "default";

  const query = params.toString();
  const cleanedUrl = changed ? `${pathname}${query ? `?${query}` : ""}${loc.hash}` : null;
  return { brand, cleanedUrl };
}

let current: Brand = "default";

export function getBrand(): Brand {
  return current;
}

export function isHealthSource(): boolean {
  return current === "hs";
}

function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = href.endsWith(".png") ? "image/png" : "image/x-icon";
  link.href = href;
}

/**
 * Call once before the router mounts: resolves the brand, strips the testing marker from the URL,
 * and sets `<html data-brand>` (which switches the CSS color tokens) and the favicon.
 */
export function initBrand(): Brand {
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  const { brand, cleanedUrl } = resolveBrand(window.location, storage);
  if (cleanedUrl !== null) window.history.replaceState(window.history.state, "", cleanedUrl);

  current = brand;
  document.documentElement.dataset.brand = brand;
  setFavicon(brand === "hs" ? "/hs/favicon.ico" : "/default/favicon-32x32.png");
  return brand;
}
